import { useLang } from 'hooks/lang';
import { useCallback } from 'react';
import { AxiosError } from 'axios';

import { useToast } from 'providers/Toast';
import type { ApiError } from 'services/api';

import { MutationErrorParser, MutationMessage, MutationMessageConfig } from './definition';
import { COMMON_MESSAGES } from 'utils/messages';

const extractApiError = (err: AxiosError): ApiError =>
  (err.response?.data || {
    message: 'Unknown Error',
  }) as ApiError;

export function useMutationMessages() {
  const { addToast } = useToast();
  const { t } = useLang();

  const parseMessage = useCallback(
    (message: MutationMessage) => {
      if (typeof message === 'string') return { title: t(message) };

      if (typeof message !== 'function') return message;

      const result = message(t);

      return typeof result === 'string' ? { title: result } : result;
    },
    [t],
  );

  const showError = useCallback(
    (
      error: AxiosError,
      {
        customParser,
        config,
      }: { customParser?: MutationErrorParser; config?: MutationMessageConfig } = {},
    ) => {
      if (config?.suppressError) return;

      if (config?.error) return addToast({ ...parseMessage(config?.error), type: 'error' });

      const data = extractApiError(error);

      const customMessage = customParser?.(data || {});

      if (customMessage) return addToast({ ...parseMessage(customMessage), type: 'error' });

      addToast({
        title: data.message.startsWith('Validation:') ? 'Validation Error' : data.message,
        type: 'error',
      });
    },
    [addToast, parseMessage],
  );

  const showSuccess = useCallback(
    (config?: MutationMessageConfig) => {
      if (config?.suppressSuccess) return;

      const message = config?.success;

      addToast({
        ...(message ? parseMessage(message) : COMMON_MESSAGES.success(t)),
        type: 'success',
      });
    },
    [addToast, parseMessage, t],
  );

  return {
    showSuccess,
    showError,
    extractApiError,
  };
}
